import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { LinkedinTypes } from 'enums';
import { OutreachTypes, SearchObjective } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import ObjectiveCard from '../../objective-card';

interface Props {
  selectedContactSearchObjective: SearchObjective | null;
  selectedLinkedinAccount?: LinkedinAccount;
  handleSelectContactSearchObjective(newObjective: SearchObjective): () => void;
}

const SelectSearchObjective: React.FC<Props> = ({ selectedContactSearchObjective, selectedLinkedinAccount, handleSelectContactSearchObjective }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const liProductType = selectedLinkedinAccount?.liProductType ?? LinkedinTypes.LINKEDIN;

  const allObjectives = [
    {
      key: 'linkedin',
      outreachType: OutreachTypes.COLD,
      title: t('linkedin_title'),
      subTitle: t('linkedin_subTitle'),
      whenToUseText: t('linkedin_whenToUseText'),
      howToUseText: t('linkedin_howToUseText'),
      exampleUrl: 'https://www.linkedin.com/search/results/people/',
      regex: /^(?:https?:\/\/)?www\.linkedin\.com\/search\/results\/people\/.*$/i,
      requiredProduct: LinkedinTypes.LINKEDIN // Available on all account types
    },
    {
      key: 'salesNavigator',
      outreachType: OutreachTypes.COLD,
      title: t('salesNavigator_title'),
      subTitle: t('salesNavigator_subTitle'),
      whenToUseText: t('salesNavigator_whenToUseText'),
      howToUseText: t('salesNavigator_howToUseText'),
      exampleUrl: 'https://www.linkedin.com/sales/search/people/',
      regex: /^(?:https?:\/\/)?www\.linkedin\.com\/sales\/search\/people(?:\/.*|\?.*)?(?!.*sharedSearchId).*$/i,
      requiredProduct: LinkedinTypes.SALES_NAV // Only for SalesNav
    },
    {
      key: 'savedSalesNav',
      outreachType: OutreachTypes.COLD,
      title: t('savedSalesNav_title'),
      subTitle: t('savedSalesNav_subTitle'),
      whenToUseText: t('savedSalesNav_whenToUseText'),
      howToUseText: t('savedSalesNav_howToUseText'),
      exampleUrl: 'https://www.linkedin.com/sales/search/people?savedSearchId=1801401363&sessionId=jesoYJPwQIeMPXYmqg5BBw%3D%3D',
      regex: /^(?:https?:\/\/)?www\.linkedin\.com\/sales\/search\/(saved\/.*|people\?.*)(?!.*sharedSearchId).*$/i,
      requiredProduct: LinkedinTypes.SALES_NAV // Only for SalesNav
    },
    {
      key: 'recruiter',
      outreachType: OutreachTypes.COLD,
      title: t('recruiter_title'),
      subTitle: t('recruiter_subTitle'),
      whenToUseText: t('recruiter_whenToUseText'),
      howToUseText: t('recruiter_howToUseText'),
      exampleUrl: 'https://www.linkedin.com/recruiter/search/people/',
      regex: /^(?:https?:\/\/)?www\.linkedin\.com\/(recruiter\/search\/people\/.*|talent\/hire\/\d+\/discover\/recruiterSearch.*|talent\/search.*)$/i,
      requiredProduct: LinkedinTypes.RECRUITER // Only for Recruiter
    },
    {
      key: 'recruiterProjects',
      outreachType: OutreachTypes.COLD,
      title: t('recruiterProjects_title'),
      subTitle: t('recruiterProjects_subTitle'),
      whenToUseText: t('recruiterProjects_whenToUseText'),
      howToUseText: t('recruiterProjects_howToUseText'),
      exampleUrl: 'https://www.linkedin.com/talent/hire/1517703338/manage/11263659531',
      regex: /^(?:https?:\/\/)?www\.linkedin\.com\/(recruiter\/project|talent\/hire)\/.*$/i,
      requiredProduct: LinkedinTypes.RECRUITER // Only for Recruiter
    },

    {
      key: 'events',
      outreachType: OutreachTypes.IN_MARKET,
      title: t('events_title'),
      subTitle: t('events_subTitle'),
      whenToUseText: t('events_whenToUseText'),
      howToUseText: t('events_howToUseText'),
      exampleUrl: 'https://www.linkedin.com/search/results/people/?eventAttending=xxx',
      regex: /^(?:https?:\/\/)?www\.linkedin\.com\/search\/results\/people\/.*$/i,
      requiredProduct: LinkedinTypes.LINKEDIN // Available on all account types
    },
    {
      key: 'postings',
      outreachType: OutreachTypes.IN_MARKET,
      title: t('postings_title'),
      subTitle: t('postings_subTitle'),
      whenToUseText: t('postings_whenToUseText'),
      howToUseText: t('postings_howToUseText'),
      exampleUrl: 'https://www.linkedin.com/search/results/content/?keywords=...',
      regex: /^(?:https?:\/\/)?www\.linkedin\.com\/(?:in\/[^/]+\/?|search\/results\/content\/.*)$/i,
      requiredProduct: LinkedinTypes.LINKEDIN // Available on all account types
    },
    {
      key: 'groups',
      outreachType: OutreachTypes.IN_MARKET,
      title: t('groups_title'),
      subTitle: t('groups_subTitle'),
      whenToUseText: t('groups_whenToUseText'),
      howToUseText: t('groups_howToUseText'),
      exampleUrl: 'https://www.linkedin.com/groups/',
      regex: /^(?:https?:\/\/)?www\.linkedin\.com\/groups\/.*$/i,
      requiredProduct: LinkedinTypes.LINKEDIN // Available on all account types
    },
    {
      key: 'jobPostings',
      outreachType: OutreachTypes.IN_MARKET,
      title: t('jobPostings_title'),
      subTitle: t('jobPostings_subTitle'),
      whenToUseText: t('jobPostings_whenToUseText'),
      howToUseText: t('jobPostings_howToUseText'),
      exampleUrl: 'https://www.linkedin.com/jobs/view/',
      regex: /^(?:https?:\/\/)?www\.linkedin\.com\/jobs\/(?:view|search)\/.*$/i,
      requiredProduct: LinkedinTypes.LINKEDIN // Available on all account types
    },
  ];

  // Filter objectives based on account type
  const objectives = useMemo(() => {
    return allObjectives.filter(objective => {
      // All LinkedIn basic options are always available
      if (objective.requiredProduct === LinkedinTypes.LINKEDIN) {
        return true;
      }

      // SalesNav options are available if account has SalesNav
      if (objective.requiredProduct === LinkedinTypes.SALES_NAV) {
        return liProductType === LinkedinTypes.SALES_NAV;
      }

      // Recruiter options are available if account has Recruiter
      if (objective.requiredProduct === LinkedinTypes.RECRUITER) {
        return liProductType === LinkedinTypes.RECRUITER;
      }

      return false;
    });
  }, [liProductType]);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1300));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down(1600));

  const columns = useMemo(() => {
    let rowsPerColumn = 3;
    if (isSmallScreen) {
      rowsPerColumn = 1;
    } else if (isMediumScreen) {
      rowsPerColumn = 2;
    }

    return _.chain(objectives)
      .map((objective, index) => ({ objective, index }))
      .groupBy(({ index }) => index % rowsPerColumn)
      .values()
      .map((column) => _.map(column, 'objective'))
      .value();
  }, [objectives, isSmallScreen, isMediumScreen]);

  const isSelected = (objective: SearchObjective) => selectedContactSearchObjective?.key === objective.key;

  return (
    <div className={classes.container}>
      <Typography className={classes.title} mb={'8px'} align={'center'}>
        {t('supportedSearches')}
      </Typography>

      <Stack className={classes.indicatorContainer} direction={'row'} gap={4} justifyContent="center">
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <div className={cx(classes.indicator, 'cold')} />
          <Typography className={classes.indicatorText}>{t('coldOutreach')}</Typography>
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <div className={cx(classes.indicator, 'in-market')} />
          <Typography className={classes.indicatorText}>{t('inMarketOutreach')}</Typography>
        </Stack>
      </Stack>

      <Stack className={classes.cardContainer} direction={'row'} gap={1.5}>
        {columns.map((column, colIndex) => (
          <Stack key={colIndex} flex={1} gap={1.5}>
            {column.map((objective, index) => (
              <ObjectiveCard
                key={index}
                selected={isSelected(objective)}
                objective={objective}
                handleSelectContactSearchObjective={handleSelectContactSearchObjective}
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: '#FFF',
    borderRadius: '12px',
    width: '100%',
    padding: '24px 12px 12px 12px',
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
  },
  indicatorContainer: {
    marginBottom: '16px',
  },
  indicator: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    '&.cold': {
      backgroundColor: theme.palette.primary.main,
    },
    '&.in-market': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  indicatorText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  cardContainer: {
    maxHeight: '400px',
    overflow: 'auto',
    paddingRight: '12px',
  },
}));

export default SelectSearchObjective;
